import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, TriangleAlert } from 'lucide-react';
import { useRequestOtpMutation, useVerifyOtpMutation, getApiError } from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { homeForRole } from '@/lib/roles';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { OtpInput } from '@/components/ui/OtpInput';
import { Logo } from '@/components/ui/Logo';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { SessionExpiredNotice } from '@/components/ui/SessionExpiredNotice';

const E164 = /^\+\d{10,15}$/;
const RESEND_SECONDS = 60;

const ERROR_NOTE =
  'flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] ' +
  'px-2.5 py-2 text-[11px] font-medium text-[hsl(var(--record-ink-danger))]';

/*
  Parent sign-in: phone number, then the code we SMS back.

  Both steps live in one component rather than two routes. The phone number is
  the OTP's only context, and putting it in a route param means a refresh on
  step two either loses it or exposes it in the URL. Keeping it in state also
  makes "wrong number, go back" instant.
*/
export function ParentLoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, setAuth } = useAuthStore();

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('+233');
  const [otp, setOtp] = useState('');
  const [localError, setLocalError] = useState('');
  const [countdown, setCountdown] = useState(0);

  const [requestOtp, requestState] = useRequestOtpMutation();
  const [verifyOtp, verifyState] = useVerifyOtpMutation();

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  if (isAuthenticated && user?.role) return <Navigate to={homeForRole(user.role)} replace />;

  const send = async () => {
    const cleaned = phone.trim();
    if (!E164.test(cleaned)) {
      setLocalError('Enter your number in international format, e.g. +233244123456');
      return;
    }
    setLocalError('');
    try {
      await requestOtp(cleaned).unwrap();
      setPhone(cleaned);
      setOtp('');
      setStep('otp');
      setCountdown(RESEND_SECONDS);
    } catch {
      /* Surfaced from requestState.error. */
    }
  };

  const submit = async (code: string) => {
    setLocalError('');
    try {
      const result = await verifyOtp({ phone, otp: code }).unwrap();
      setAuth(result.accessToken, result.user);
      navigate(homeForRole(result.user.role), { replace: true });
    } catch {
      setOtp('');
    }
  };

  const serverError =
    step === 'phone'
      ? requestState.error && getApiError(requestState.error)
      : verifyState.error && getApiError(verifyState.error);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[hsl(218_52%_13%)] px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-5 flex items-center gap-2.5">
          <Logo size={30} tone="reversed" className="flex-shrink-0" />
          <div>
            <p className="text-[15px] font-bold leading-tight text-white">SafePass</p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-white/45">
              Parent &amp; guardian
            </p>
          </div>
        </div>

        <SessionExpiredNotice />

        <div className="rounded-lg border border-white/10 bg-card p-5">
          {step === 'phone' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
              noValidate
            >
              <h1 className="text-[15px] font-bold tracking-tight text-foreground">
                Enter your phone number
              </h1>
              <p className={cn(styles.meta, 'mt-1')}>
                We'll text you a 6-digit code. Use the number the school has on file for your child —
                if it isn't registered, we can't send you a code.
              </p>

              <div className="mt-4 flex flex-col gap-3">
                <Field
                  label="Phone number"
                  htmlFor="parent-phone"
                  error={localError || (serverError as string) || undefined}
                  required
                >
                  <Input
                    id="parent-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    autoFocus
                    placeholder="+233244123456"
                    value={phone}
                    invalid={Boolean(localError || serverError)}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Field>

                <Button type="submit" variant="primary" size="lg" loading={requestState.isLoading} className="w-full">
                  {requestState.isLoading ? 'Sending…' : 'Send my code'}
                </Button>
              </div>
            </form>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setLocalError('');
                }}
                className="mb-3 flex items-center gap-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft size={12} />
                Change number
              </button>

              <h1 className="text-[15px] font-bold tracking-tight text-foreground">Enter the code</h1>
              <p className={cn(styles.meta, 'mt-1')}>
                Sent to <span className="font-mono font-semibold text-foreground">{phone}</span>. It
                expires in 5 minutes.
              </p>

              <div className="mt-4 flex flex-col gap-3">
                <OtpInput
                  value={otp}
                  onChange={setOtp}
                  onComplete={(code) => void submit(code)}
                  disabled={verifyState.isLoading}
                  invalid={Boolean(serverError)}
                  autoFocus
                />

                {serverError && (
                  <div role="alert" className={ERROR_NOTE}>
                    <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
                    {serverError as string}
                  </div>
                )}

                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  loading={verifyState.isLoading}
                  disabled={otp.length !== 6}
                  onClick={() => void submit(otp)}
                >
                  {verifyState.isLoading ? 'Checking…' : 'Verify'}
                </Button>

                <div className="text-center">
                  {countdown > 0 ? (
                    <p className="text-[11px] text-muted-foreground">
                      Didn't get it? You can ask again in{' '}
                      <span className="font-semibold tabular-nums text-foreground">{countdown}s</span>
                    </p>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      loading={requestState.isLoading}
                      onClick={() => void send()}
                    >
                      Send a new code
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-[11px] text-white/45">
          Staff member?{' '}
          <Link
            to="/login?as=gate"
            className="font-semibold text-white/80 underline decoration-white/30 underline-offset-2 transition-colors hover:text-white"
          >
            Sign in with a staff account
          </Link>
        </p>
      </div>
    </div>
  );
}
